/**
 * Rob Steins UI helpers shared by the new sections.
 *
 *  <rs-slider>          A horizontal scroller with arrow buttons ([data-rs-prev] / [data-rs-next]) around a
 *                       [data-rs-track] element. Arrows disable at the ends. Also drag-scrolls with a mouse.
 *  [data-rs-reveal]     Fades and lifts an element in when it scrolls into view. --rs-d sets a stagger delay.
 *  [data-rs-parallax]   Sets --mx / --my (-1 to 1) from the pointer so children can drift with the mouse.
 *  [data-rs-count-up]   Counts a number up when it scrolls into view ("100" with data-suffix="k+").
 *  [data-rs-tilt]       A gentle 3D tilt on hover.
 *  [data-rs-sound]      A mute / unmute button for the video next to it (customer video tiles).
 *  [data-rs-sparkle]    Little stars follow the mouse inside the element (it needs position: relative).
 *
 * Everything respects prefers-reduced-motion, and nothing is hidden in the theme editor.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------- slider */
class RsSlider extends HTMLElement {
  #track = /** @type {HTMLElement | null} */ (null);
  #raf = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-rs-track]');
    if (!this.#track) return;

    this.querySelector('[data-rs-prev]')?.addEventListener('click', () => this.#scroll(-1));
    this.querySelector('[data-rs-next]')?.addEventListener('click', () => this.#scroll(1));
    this.#track.addEventListener('scroll', this.#queueUpdate, { passive: true });
    new ResizeObserver(this.#queueUpdate).observe(this.#track);
    this.#dragToScroll(this.#track);
    this.#update();
  }

  /** @param {number} direction */
  #scroll(direction) {
    const track = this.#track;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.7, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  #queueUpdate = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const prev = this.querySelector('[data-rs-prev]');
    const next = this.querySelector('[data-rs-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= max - 2;
    this.toggleAttribute('data-rs-scrollable', max > 4);
  }

  /** @param {HTMLElement} track */
  #dragToScroll(track) {
    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });
    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      dragging = false;
    });
    // a drag should not count as a click on a link inside the track
    track.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      true
    );
  }
}

if (!customElements.get('rs-slider')) customElements.define('rs-slider', RsSlider);

/* ---------------------------------------------------------------- video sound button */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-rs-sound]') : null;
  if (!button) return;
  // the tile can be a link: pressing the sound button must not follow it
  event.preventDefault();
  event.stopPropagation();
  const video = button.parentElement?.querySelector('video');
  if (!(video instanceof HTMLVideoElement)) return;
  video.muted = !video.muted;
  button.setAttribute('aria-pressed', String(!video.muted));
  button.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
  if (!video.muted) video.play().catch(() => undefined);
});

/* ---------------------------------------------------------------- reveal, count-up, parallax, tilt */
const inEditor = document.documentElement.classList.contains('shopify-design-mode');

/** @param {HTMLElement} el */
function countUp(el) {
  const to = parseFloat(el.dataset.rsCountUp || '0');
  const decimals = Number(el.dataset.decimals || 0);
  const suffix = el.dataset.suffix || '';
  if (!Number.isFinite(to) || reduceMotion.matches) {
    el.textContent = to.toLocaleString('en-US', { maximumFractionDigits: decimals }) + suffix;
    return;
  }
  const start = performance.now();
  const duration = 1600;
  const tick = (now) => {
    const k = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - k, 3);
    el.textContent = (to * eased).toLocaleString('en-US', { maximumFractionDigits: decimals, minimumFractionDigits: decimals }) + suffix;
    if (k < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = /** @type {HTMLElement} */ (entry.target);
            el.classList.add('is-in');
            if (el.hasAttribute('data-rs-count-up')) countUp(el);
            revealObserver?.unobserve(el);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

/** @param {ParentNode} root */
function scan(root) {
  for (const el of root.querySelectorAll('[data-rs-reveal], [data-rs-count-up]')) {
    if (!(el instanceof HTMLElement) || el.dataset.rsSeen) continue;
    el.dataset.rsSeen = '1';
    if (!revealObserver || inEditor || reduceMotion.matches) {
      el.classList.add('is-in');
      if (el.hasAttribute('data-rs-count-up')) countUp(el);
    } else {
      revealObserver.observe(el);
    }
  }

  for (const el of root.querySelectorAll('[data-rs-parallax]')) {
    if (!(el instanceof HTMLElement) || el.dataset.rsBound || reduceMotion.matches) continue;
    el.dataset.rsBound = '1';
    el.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', (((event.clientX - r.left) / r.width - 0.5) * 2).toFixed(3));
      el.style.setProperty('--my', (((event.clientY - r.top) / r.height - 0.5) * 2).toFixed(3));
    });
    el.addEventListener('pointerleave', () => {
      el.style.setProperty('--mx', '0');
      el.style.setProperty('--my', '0');
    });
  }

  for (const el of root.querySelectorAll('[data-rs-sparkle]')) {
    if (!(el instanceof HTMLElement) || el.dataset.rsSparkleBound || reduceMotion.matches) continue;
    el.dataset.rsSparkleBound = '1';
    const colors = ['#f29bb2', '#f2c94c', '#b9a3ee', '#ffffff', '#f7a8c4'];
    let last = 0;
    el.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const now = performance.now();
      if (now - last < 60) return;
      last = now;
      const r = el.getBoundingClientRect();
      const spark = document.createElement('i');
      spark.className = 'rs-trail';
      spark.style.left = `${event.clientX - r.left}px`;
      spark.style.top = `${event.clientY - r.top}px`;
      spark.style.setProperty('--s', `${8 + Math.random() * 10}px`);
      spark.style.setProperty('--dx', `${Math.random() * 44 - 22}px`);
      spark.style.setProperty('--dy', `${14 + Math.random() * 36}px`);
      spark.style.setProperty('--c', colors[Math.floor(Math.random() * colors.length)]);
      el.appendChild(spark);
      spark.addEventListener('animationend', () => spark.remove(), { once: true });
    });
  }

  for (const el of root.querySelectorAll('[data-rs-tilt]')) {
    if (!(el instanceof HTMLElement) || el.dataset.rsTiltBound || reduceMotion.matches) continue;
    el.dataset.rsTiltBound = '1';
    el.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const r = el.getBoundingClientRect();
      const x = (event.clientX - r.left) / r.width - 0.5;
      const y = (event.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(700px) rotateX(${(-y * 7).toFixed(2)}deg) rotateY(${(x * 9).toFixed(2)}deg) translateY(-4px)`;
    });
    el.addEventListener('pointerleave', () => {
      el.style.transform = '';
    });
  }
}

scan(document);

// Sections re-render in the theme editor and content loads in later (filters, load more): pick those up too.
/** @type {ReturnType<typeof setTimeout> | undefined} */
let scanTimer;
new MutationObserver(() => {
  clearTimeout(scanTimer);
  scanTimer = setTimeout(() => scan(document), 80);
}).observe(document.body, { childList: true, subtree: true });
